import { envConfigs } from '../../../envConfigs';
import { mapLocalLinks } from '../../../helpers/localDevHelper';

export const INIT_RECOMMENDATIONS = 'INIT_RECOMMENDATIONS';
export const LAUNCH_RECOMMENDATION = 'LAUNCH_RECOMMENDATION';
export const SEND_MOBILE_EMAIL = 'SEND_MOBILE_EMAIL';

const CLEAR_LAUNCHPAD = 'CLEAR_LAUNCHPAD';
const CLEAR_NPS_PROMPT = 'CLEAR_NPS_PROMPT';
const CLEAR_OPTIONAL_TRIGGER = 'CLEAR_OPTIONAL_TRIGGER';
const SET_MOBILE_EMAIL_STATUS = 'SET_MOBILE_EMAIL_STATUS';
const SET_RECOMMENDATIONS = 'SET_RECOMMENDATIONS';
const SET_RECOMMENDATIONS_LOADED = 'SET_RECOMMENDATIONS_LOADED';
export const INIT_DASHBOARD = 'INIT_DASHBOARD';
const SET_DASHBOARD = 'SET_DASHBOARD';
const SET_DASHBOARD_LOADING = 'SET_DASHBOARD_LOADING';
const SET_SKIP_PLACEMENT_POPUP = 'SET_SKIP_PLACEMENT_POPUP';

export const EMAIL_STATUS = {
  INVALID: 'INVALID',
  NOT_SENT: 'NOT_SENT',
  SEND_ATTEMPTED: 'SEND_ATTEMPTED',
  SENT_SUCCESSFULLY: 'SENT_SUCCESSFULLY',
  SENT_UNSUCCESSFULLY: 'SENT_UNSUCCESSFULLY'
};

const initialState = {
  days_until_trigger: 0,
  editorClient: envConfigs.editorClient,
  hasNoAccess: true,
  mobileEmailLauncher: false,
  mobileEmailLauncherType: '',
  mobileEmailStatus: EMAIL_STATUS.NOT_SENT,
  optional_warning: false,
  optional_trigger_recommendation: null,
  optional_trigger_is_adaptive_beta: false,
  recommendations: [],
  recommendationsLoaded: false,
  skipPlacementPopup: { show: false, recommendation: {}},
  dashboard: [],
  dashboardLoading: false
};

/*
  takes the recommendations from LPS and unpacks them, with adjustments,
  into an object whose properties are spread into the launchpad store
 */
const processRecommendations = (recommendationsRaw, userInfo) => {
  let recommendations = [];
  let launchpadProperties = { ...initialState };
  delete launchpadProperties.dashboard;

  if (recommendationsRaw) {
    mapLocalLinks(recommendationsRaw);
    let optionalTriggeredProductName = '';
    recommendationsRaw.map((rawRecommendation) => {
      if (!rawRecommendation.optional) {
        recommendations.push(rawRecommendation);
      } else {
        if (rawRecommendation.warning) {
          launchpadProperties.days_until_trigger = rawRecommendation.days_until_trigger;
          launchpadProperties.optional_warning = true;
        } else {
          // consider if multiple proficiency tests are triggered at a given time, must take them in the right order
          if (launchpadProperties.optional_trigger) {
            // a test has already been recommended - if it is higher in number than this one, replace it
            if (
              rawRecommendation.product_name.indexOf('Proficiency Test') !== -1 &&
              optionalTriggeredProductName.indexOf('Proficiency Test') !== -1 &&
              rawRecommendation.product_name < optionalTriggeredProductName
            ) {
              optionalTriggeredProductName = rawRecommendation.product_name;
              launchpadProperties.optional_trigger_recommendation = rawRecommendation.recommendation;
            }
          } else {
            // this is the first test being recommended
            optionalTriggeredProductName = rawRecommendation.product_name;
            launchpadProperties.optional_trigger_recommendation = rawRecommendation.recommendation;
            launchpadProperties.optional_trigger_is_adaptive_beta =
              rawRecommendation.product_name.indexOf('Adaptive') !== -1;
          }
          launchpadProperties.optional_trigger = true;
        }
      }
      // evaluate for email section
      if (rawRecommendation.sub_product_id) {
        launchpadProperties.mobileEmailLauncher = true;
        launchpadProperties.mobileEmailLauncherType = rawRecommendation.product_name;
      }
      else if (rawRecommendation.show_email_launcher === true) {
        launchpadProperties.mobileEmailLauncher = true;
        launchpadProperties.mobileEmailLauncherType = rawRecommendation.email_product_name;
      }
      // Prompt for NPS?
      if (rawRecommendation.collect_nps) {
        launchpadProperties.collect_nps = true;
      }
      return rawRecommendation;
    });
  }
  launchpadProperties.hasNoAccess = recommendations.length === 0;
  launchpadProperties.recommendations = recommendations;
  launchpadProperties.recommendationsLoaded = true;
  return launchpadProperties;
};

const processDashboard = (dashboardRaw) => {
  const msToTime = (duration) => {
    let minutes = parseInt((duration / (1000 * 60)) % 60, 10);
    let hours = parseInt(duration / (1000 * 60 * 60), 10);
    minutes = minutes < 10 ? '0' + minutes : minutes;
    return hours + ':' + minutes;
  };

  let dashboard = dashboardRaw;

  if (dashboard) {
    dashboard.isLearner = dashboardRaw.isLearner;
    if (dashboardRaw.allTimeActivities) {
      dashboard.allTimeMobile = msToTime(dashboardRaw.allTimeActivities.mobileTimeSpentMs);
      dashboard.allTimeOnline = msToTime(dashboardRaw.allTimeActivities.elearningTimeSpentMs);
      dashboard.allTimeTutoring = msToTime(dashboardRaw.allTimeActivities.tutoringTimeSpentMs);
      dashboard.allTimeTotal = msToTime(dashboardRaw.allTimeActivities.totalTimeSpentMs);

      dashboard.showDashboardTutoring = dashboardRaw.allTimeActivities.tutoringTimeSpentMs > 0;
      dashboard.showDashboard = dashboardRaw.allTimeActivities.totalTimeSpentMs > 0;
    }

    if (dashboardRaw.monthToDateActivities) {
      dashboard.monthMobile = msToTime(dashboardRaw.monthToDateActivities.mobileTimeSpentMs);
      dashboard.monthOnline = msToTime(dashboardRaw.monthToDateActivities.elearningTimeSpentMs);
      dashboard.monthTutoring = msToTime(dashboardRaw.monthToDateActivities.tutoringTimeSpentMs);
      dashboard.monthTotal = msToTime(dashboardRaw.monthToDateActivities.totalTimeSpentMs);
    }
  }

  return dashboard;
};

export const launchpadReducer = (launchpadState = initialState, action = null) => {
  switch (action && action.type) {
    case CLEAR_LAUNCHPAD:
      return initialState;

    case SEND_MOBILE_EMAIL:
    case LAUNCH_RECOMMENDATION:
      return launchpadState;

    case INIT_RECOMMENDATIONS:
      // associated saga fetches recommendations and then dispatches action for SET_RECOMMENDATIONS
      return launchpadState;

    // this action in effect calls setRecommendationsLoaded(true) as part of processRecommendations()
    case SET_RECOMMENDATIONS:
      return {
        ...launchpadState,
        ...processRecommendations(action.payload.recommendations, action.payload.userInfo)
      };

    case SET_RECOMMENDATIONS_LOADED:
      return {
        ...launchpadState,
        recommendationsLoaded: action.payload.loaded
      };

    case CLEAR_NPS_PROMPT:
      return {
        ...launchpadState,
        collect_nps: false
      };

    case CLEAR_OPTIONAL_TRIGGER:
      return {
        ...launchpadState,
        optional_trigger: false
      };

    case SET_MOBILE_EMAIL_STATUS:
      return {
        ...launchpadState,
        mobileEmailStatus: action.payload.emailStatus
      };

    case SET_DASHBOARD:
      return {
        ...launchpadState,
        dashboard: { ...processDashboard(action.payload.dashboard) }
      };

    case SET_DASHBOARD_LOADING:
      return {
        ...launchpadState,
        dashboardLoading: action.payload.dashboardLoading
      };

    case SET_SKIP_PLACEMENT_POPUP:
      return {
        ...launchpadState,
        skipPlacementPopup: action.payload
      }

    default:
      return launchpadState;
  }
};

export const launchpadSelectors = {
  daysUntilTrigger: (state) => state.launchpad.days_until_trigger,
  recommendations: (state) => state.launchpad.recommendations,
  recommendationsLoaded: (state) => state.launchpad.recommendationsLoaded,
  skipPlacementPopup: (state) => state.launchpad.skipPlacementPopup,

  dashboard: (state) => state.launchpad.dashboard,
  dashboardLoading: (state) => state.launchpad.dashboardLoading,
  
  mobileEmailLauncher: (state) => state.launchpad.mobileEmailLauncher,
  mobileEmailLauncherType: (state) => state.launchpad.mobileEmailLauncherType,
  mobileEmailStatus: (state) => state.launchpad.mobileEmailStatus,
  optionalWarning: (state) => state.launchpad.optional_warning,
  optionalTrigger: (state) => state.launchpad.optional_trigger,
  optionalTriggerIsAdaptiveBeta: (state) => state.launchpad.optional_trigger_is_adaptive_beta,
  optionalTriggerRecommendation: (state) => state.launchpad.optional_trigger_recommendation
};

export const launchpadActions = {
  setRecommendations: (recommendations, userInfo) => ({
    type: SET_RECOMMENDATIONS,
    payload: { recommendations: recommendations, userInfo: userInfo }
  }),
  setRecommendationsLoaded: (loaded) => ({ type: SET_RECOMMENDATIONS_LOADED, payload: { loaded } }),
  initDashboard: () => ({ type: INIT_DASHBOARD }),
  setDashboard: (dashboard) => ({ type: SET_DASHBOARD, payload: { dashboard } }),
  setDashboardLoading: (dashboardLoading) => ({ type: SET_DASHBOARD_LOADING, payload: { dashboardLoading } }),
  clearLaunchpad: () => ({ type: CLEAR_LAUNCHPAD }),
  clearOptionalTrigger: () => ({ type: CLEAR_OPTIONAL_TRIGGER }),
  sendMobileEmail: (emailToSendTo) => ({ type: SEND_MOBILE_EMAIL, payload: { emailToSendTo: emailToSendTo } }),
  setMobileEmailStatus: (emailStatus) => ({ type: SET_MOBILE_EMAIL_STATUS, payload: { emailStatus: emailStatus } }),
  setSkipPlacementPopup: (show, recommendation) => ({ type: SET_SKIP_PLACEMENT_POPUP, payload: { show, recommendation } }),
  initRecommendations: () => ({ type: INIT_RECOMMENDATIONS }),
  launchRecommendation: (recommendation, isMobile) => ({
    type: LAUNCH_RECOMMENDATION,
    payload: { recommendation, isMobile }
  })
};



// WEBPACK FOOTER //
// ./src/modules/launchpad/reducer/launchpad-reducer.js